import React, { useState } from "react";
import { StyledBox, StyledHr } from "../ContactOverview/ContactOverview";
import StyledText from "../../../components/StyledComponents/StyledText";
import { CorporateTheme } from "../../../utils/themes";
import { DashedBorder } from "../ContactOverview/ContactAddresses";
import PlusIcon from "../../../assets/icons/PlusIcon";
import { Button } from "../../../components/form/Button";
import Avatar from "../../../components/Avatar/Avatar";
import EditIcon from "../../../assets/icons/EditIcon";
import ContactPlaceHolder from "../../../assets/images/contactPlaceHolder.png";
import {
  useAddNewNoteMutation,
  useDeleteNoteMutation,
  useGetPrivateNotesByIdQuery,
  useUpdateNoteMutation,
} from "../../../api/contactApiSlice";
import { useParams } from "react-router-dom";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import { isObject } from "lodash";
import { useToast } from "../../../components/toast/ToastContext";
import DeletePrompt, {
  ActionButton,
  Subtitle,
  Title,
} from "../../../components/DeletePrompt";
import StyledTextEditor from "../../../components/StyledComponents/TextEditor/StyledTextEditor";
import CrossIcon from "../../../assets/icons/CrossIcon";
import LoadingCircleIcon from "../../../assets/icons/LoadingCircleIcon";
import { useTheme } from "../../../middleware/ThemeContext";
import SaveIcon from "../../../assets/icons/SaveIcon";

const PrivateNotes = ({ refetch: modifiedDateRefetch }: { refetch: () => void }) => {
  const { theme } = useTheme();
  const showToast = useToast();
  const { id } = useParams();
  const user = localStorage.getItem("authData");
  const authData = JSON.parse(user || "{}");
  const [loading, setLoading] = useState(false);
  const [addNewNote, setAddNewNote] = useState(false);
  const [updateNote, setUpdateNote] = useState(false);
  const [deleteNote, setDeleteNote] = useState(false);
  const [noteId, setNoteId] = useState();
  const [deleteNotte] = useDeleteNoteMutation();
  const [addNote] = useAddNewNoteMutation();
  const [updateNotes] = useUpdateNoteMutation();

  const { data: privateNotes, refetch } = useGetPrivateNotesByIdQuery(
    {
      id: id,
      module: "CONTACT",
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const addNotes = (val: string) => {
    const plainText = getPlainText(val);

    if (!plainText) {
      setError("Note content cannot be empty.");
      return;
    }
    setError("");
    setLoading(true);
    const values = {
      content: val,
      contactId: Number(id),
      id: noteId,
    };

    const call = updateNote ? updateNotes : addNote;

    try {
      call(values).then((response: any) => {
        if (response?.data && isObject(response?.data)) {
          refetch();
          setLoading(false);
          setAddNewNote(false);
          setUpdateNote(false);
          setValue("");
          modifiedDateRefetch();
          showToast(
            "Success",
            ` Notes ${updateNote ? "updated" : "added"} successfully`,
            "success"
          );
        } else {
          setLoading(false);
          showToast(
            "Action Failed",
            response?.data?.message || `Error ${updateNote ? "updating" : "adding"} notes`,
            "error"
          );
        }
      });
    } catch (e) {
      console.log(e);
    }
  };

  const handleDeleteNotes = async () => {
    const { data }: any = await deleteNotte(noteId);
    if (data) {
      showToast("Success", data.message, "success");
      refetch();
      setDeleteNote(false);
      modifiedDateRefetch();
    }
  };

  const [error, setError] = useState("");
  const [value, setValue] = useState("");

  return (
    <StyledBox theme={theme} className="px-3">
      <div className="d-flex align-items-center justify-content-between">
        <StyledText
          fontSize="16px"
          fontWeight={700}
          textColor={theme.CorporateTheme.colors.headerColor}
          className="mb-0 py-3"
        >
          Notes
        </StyledText>
      </div>
      <StyledHr theme={theme} />
      {addNewNote || updateNote ? (
        <>
          <div className="d-flex flex-column align-items-center justify-content-center py-2">
            <StyledText
              fontSize="16px"
              fontWeight={700}
              textColor={theme.CorporateTheme.colors.primary}
              className="mb-2"
            >
              Private Notes
            </StyledText>
            <StyledText
              fontSize="14px"
              fontWeight={500}
              textColor={theme.CorporateTheme.colors.textSecondary}
              className="mb-2 text-center"
            >
              These are private notes. Only L2s from the same organization can
              view or add new notes.
            </StyledText>
          </div>

          <StyledTextEditor
            value={value}
            onChange={(val) => {
              setValue(val);
              if (error) setError("");
            }}
            placeholder={"Add Notes"}
            isReadOnly={false}
            minHeight={"300px"}
            isInvalid={!!error}
          />
          {error && (
            <StyledText fontSize="12px" textColor="red">
              {error}
            </StyledText>
          )}

          <div className="d-flex gap-2 justify-content-end align-items-center my-3">
            <Button
              theme={theme}
              $variant="cancel"
              onClick={() => {
                setAddNewNote(false);
                setUpdateNote(false);
                setValue("");
              }}
              disabled={loading}
            >
              <CrossIcon />
              <label> Cancel</label>
            </Button>
            <Button theme={theme} $variant="customButton" btnBg={theme.CreateFormTheme.colors.primaryBtn} color={theme.CorporateTheme.colors.background} onClick={() => addNotes(value)}>
              {loading ? (
                <>
                  <LoadingCircleIcon color={theme.CorporateTheme.colors.background} />
                </>
              ) : (
                <>
                  <SaveIcon color={theme.CorporateTheme.colors.background} />
                </>
              )}
              <label>Save</label>
            </Button>
          </div>
          <StyledHr theme={theme} />
        </>
      ) : (
        <DashedBorder theme={theme} className="justify-content-center d-flex align-items-center p-3">
          <StyledText
            fontSize="16px"
            fontWeight={700}
            textColor={theme.CorporateTheme.colors.primary}
            className="mb-2"
          >
            Private Notes
          </StyledText>
          <StyledText
            fontSize="14px"
            fontWeight={500}
            textColor={theme.CorporateTheme.colors.textSecondary}
            className="mb-2 text-center"
          >
            These are private notes. Only L2s from the same organization can
            view or add new notes.
          </StyledText>
          <Button
            theme={theme}
            type="button"
            $variant="add"
            className="w-100"
            onClick={() => setAddNewNote(true)}
          >
            <PlusIcon color={theme.CorporateTheme.colors.background} /> Add New Note
          </Button>
        </DashedBorder>
      )}

      <div className="mt-3">
        {privateNotes?.privateNotes?.map((notes: any, index: number) => (
          <div className="d-flex flex-column gap-2" key={index}>
            <div className="d-flex align-items-center justify-content-between ">
              <div className="d-flex gap-1 align-items-center">
                <Avatar
                  name={notes?.createdByAccount?.firstName}
                  imageUrl={
                    notes?.createdByAccount?.image || ContactPlaceHolder
                  }
                  size={34}
                />
                <StyledText
                  fontSize="14px"
                  fontWeight={600}
                  textColor={theme.CorporateTheme.colors.headerColor}
                  className="mb-1 "
                >
                  {notes?.createdByAccount?.firstName +
                    " " +
                    notes?.createdByAccount?.lastName}
                </StyledText>
              </div>
              <div className="d-flex gap-1 align-items-center">
                {/* {authData?.userId === notes?.createdByAccount?.id && (
                  <>
                    <div
                      className="d-flex align-items-center gap-1 cursor-pointer"
                      onClick={() => {
                        setDeleteNote(true);
                        setNoteId(notes?.id);
                      }}
                    >
                      <DeleteIcon />
                    </div>
                    <div
                      className="d-flex align-items-center gap-1 cursor-pointer"
                      onClick={() => {
                        setUpdateNote(true);
                        setValue(notes?.content);
                        setNoteId(notes?.id);
                      }}
                    >
                      <EditIcon size={16} />
                    </div>
                  </>
                )} */}

                <div>
                  <StyledText
                    fontSize="13px"
                    fontWeight={400}
                    className="font-lato"
                    textColor={theme.CorporateTheme.colors.headerColor}
                  >
                    {new Date(notes?.createdAt).toLocaleDateString("en-US", {
                      weekday: "short",
                      hour: "numeric",
                      minute: "numeric",
                      hour12: true,
                    })}
                  </StyledText>
                </div>
              </div>
            </div>
            <StyledText
              fontSize="14px"
              fontWeight={350}
              textColor={theme.CorporateTheme.colors.textSecondary}
              className="mb-1"
              dangerouslySetInnerHTML={{ __html: notes?.content || "" }}
            />
            {/* {getPlainText(notes?.content)}
            </StyledText> */}
            <StyledHr theme={theme} />
          </div>
        ))}
      </div>
      {/* {addNewNote && (
        <AddNewNoteModal
          onCancel={() => setAddNewNote(false)}
          onConfirm={(val: string) => addNotes(val)}
          loading={loading}
        />
      )} */}

      {deleteNote && (
        <DeletePrompt
          title="Are you sure you want to delete tha note?"
          subTitle="Deleting this record
            will remove all associated data, including events, certifications,
            memberships, and more."
          onCancel={() => setDeleteNote(false)}
          onConfirm={handleDeleteNotes}
        />
      )}
    </StyledBox>
  );
};

const getPlainText = (html: string) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.innerText.trim(); // plain text
};

export default PrivateNotes;
